import type { ReactNode } from "react";
import { useScrollReveal } from "@/hooks/useScrollReveal";

export interface WorkflowStage {
  key: string;
  label: string;
  icon: string;
  status: "completed" | "current" | "pending" | "blocked" | "overdue";
  detail?: string;
  onClick?: () => void;
}

interface WorkflowPathProps {
  stages: WorkflowStage[];
  title?: string;
  onStageClick?: (stage: WorkflowStage) => void;
  animate?: boolean;
}

function stageColors(status: string) {
  switch (status) {
    case "completed": return { dot: "bg-emerald-500", line: "bg-emerald-300", text: "text-emerald-700", bg: "bg-emerald-50", border: "border-emerald-200", icon: "text-emerald-500" };
    case "current": return { dot: "bg-primary-500 animate-pulse", line: "bg-primary-300", text: "text-primary-700", bg: "bg-primary-50", border: "border-primary-200", icon: "text-primary-500" };
    case "blocked": return { dot: "bg-foreground-300", line: "bg-foreground-200", text: "text-foreground-500", bg: "bg-foreground-50", border: "border-foreground-200", icon: "text-foreground-400" };
    case "overdue": return { dot: "bg-red-500 animate-pulse", line: "bg-red-300", text: "text-red-700", bg: "bg-red-50", border: "border-red-200", icon: "text-red-500" };
    default: return { dot: "bg-foreground-200", line: "bg-foreground-100", text: "text-foreground-400", bg: "bg-foreground-50", border: "border-foreground-100", icon: "text-foreground-300" };
  }
}

export default function WorkflowPath({ stages, title, onStageClick, animate = true }: WorkflowPathProps) {
  const ref = useScrollReveal<HTMLDivElement>({
    selector: ".workflow-stage",
    staggerDelay: 100,
    threshold: 0.15,
  });

  if (!stages || stages.length === 0) return null;

  return (
    <div className={`w-full ${animate ? "scroll-reveal" : ""}`} ref={animate ? ref : undefined}>
      {title && (
        <p className="text-xs font-bold text-foreground-400 uppercase tracking-wider mb-4">{title}</p>
      )}
      <div className="flex items-start gap-0 overflow-x-auto pb-2">
        {stages.map((stage, idx) => {
          const colors = stageColors(stage.status);
          const isLast = idx === stages.length - 1;
          const completed = stage.status === "completed";

          return (
            <div key={stage.key} className={`flex items-start flex-shrink-0 workflow-stage ${animate ? "scroll-reveal" : ""}`} style={{ minWidth: 0, transitionDelay: `${idx * 80}ms` }}>
              {/* Stage node */}
              <div className="flex flex-col items-center gap-1.5" style={{ minWidth: "80px" }}>
                <button
                  onClick={() => {
                    if (stage.onClick) stage.onClick();
                    else if (onStageClick) onStageClick(stage);
                  }}
                  className={`relative flex items-center justify-center w-10 h-10 rounded-xl transition-all duration-300 cursor-pointer ${
                    stage.status === "current"
                      ? `${colors.bg} ${colors.border} border shadow-soft ring-2 ring-offset-2 ring-primary-200/40`
                      : completed
                        ? `${colors.bg} ${colors.border} border shadow-soft`
                        : stage.status === "blocked" || stage.status === "overdue"
                          ? `${colors.bg} ${colors.border} border`
                          : "bg-background-50 border border-background-200"
                  }`}
                  title={stage.detail || stage.label}
                >
                  <span className={`flex items-center justify-center w-4 h-4 ${colors.icon}`}>
                    {completed ? (
                      <i className="ri-check-line text-sm" />
                    ) : (
                      <i className={`${stage.icon} text-sm`} />
                    )}
                  </span>
                  {stage.status === "overdue" && (
                    <span className="absolute -top-1 -right-1 w-3 h-3 rounded-full bg-red-500 border-2 border-white animate-pulse" />
                  )}
                </button>
                <span className={`text-[10px] font-semibold text-center leading-tight px-1 ${colors.text} max-w-[90px] truncate`}>
                  {stage.label}
                </span>
                {stage.detail && (
                  <span className="text-[9px] text-foreground-400 text-center leading-tight max-w-[90px] truncate -mt-0.5">
                    {stage.detail}
                  </span>
                )}
              </div>

              {/* Connector line */}
              {!isLast && (
                <div className="flex items-center pt-5 px-0.5">
                  <div className={`h-0.5 w-8 md:w-12 rounded-full transition-all duration-500 ${
                    completed ? colors.line : "bg-foreground-100"
                  }`} />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}